/**
 * Contract -> HTML for TipTap.
 *
 * Blocks are already in reading order, so rendering is a single walk of the
 * array. `parent` is only consulted to work out heading depth.
 *
 * Everything is emitted as nodes TipTap's schema actually holds (p, h1-h6,
 * table, img, and our mathBlock). Wrapper `<div>`s and `class` attributes are
 * avoided on purpose: ProseMirror discards nodes and attributes its schema does
 * not know about, which is why the previous `<div data-block-id>` markup lost
 * its handles. Identity travels on `data-*` attributes registered by the
 * BlockAttributes extension.
 */
import {
  BlockSchema,
  formatZodError,
  ManuscriptContractShapeSchema,
} from '../schemas/contract';

import {
  escapeAttribute,
  escapeText,
  renderContentValue,
  sanitizeHtml,
} from './markdown';

import type { Block, ManuscriptContract } from '../types/contract';

/** One block that failed validation or crashed its renderer. */
export interface BlockRenderIssue {
  index: number;
  key: string;
  type: string | null;
  reason: 'invalid_schema' | 'render_error';
  message: string;
}

export interface RenderOptions {
  /**
   * Map a contract `href` (typically `s3://...`) to something the browser can
   * load directly. Without one (or if it returns nothing), the raw href is
   * emitted as-is — the `FigureImage` node view resolves `s3://` hrefs itself
   * via the document's media-access grant (`DocumentContext`).
   */
  resolveHref?: (href: string) => string | null;
  /**
   * Called once per block that fails schema validation or throws while
   * rendering. The block still renders — as an inline error placeholder — so
   * one bad block never blanks the rest of the document.
   */
  onBlockIssue?: (issue: BlockRenderIssue) => void;
}

/**
 * Render-time handle for a block.
 *
 * Extracted-metadata blocks are keyed by their role — that is how the left nav
 * addresses them, and a role is unique per manuscript by contract rule. Ordinary
 * prose has no role, so it falls back to its position. An explicit `id` wins
 * over both once the extractor starts emitting them.
 */
export function blockKey(block: Block, index: number): string {
  return block?.id ?? block?.role ?? `b${index}`;
}

/**
 * `title` may carry formatting markup (e.g. `<strong>Funding</strong>`), but
 * `parent` -- on both section and paragraph blocks -- always references a
 * section by its plain text. Hierarchy matching has to compare like with
 * like, so every lookup below goes through this first.
 */
function plainText(value: string): string {
  return value.replace(/<[^>]+>/g, '').trim();
}

/**
 * Heading level for each section title. Top-level sections are h2; h1 is
 * reserved for the article title.
 */
function computeHeadingLevels(blocks: readonly Block[]): Map<string, number> {
  const parentOf = new Map<string, string | null>();

  for (const block of blocks) {
    if (block?.type === 'section' && block.title) {
      parentOf.set(plainText(block.title), block.parent);
    }
  }

  const levels = new Map<string, number>();
  const depthOf = (title: string, seen: Set<string>): number => {
    const cached = levels.get(title);

    if (cached !== undefined) return cached;
    // Guard against a cycle from a self- or mutually-referencing parent title.
    if (seen.has(title)) return 2;
    seen.add(title);

    const parent = parentOf.get(title);
    const level = parent ? Math.min(depthOf(parent, seen) + 1, 6) : 2;

    levels.set(title, level);
    return level;
  };

  for (const title of parentOf.keys()) depthOf(title, new Set());
  return levels;
}

/**
 * Scratch element reused across `applyBlockAttributes` calls. `renderContract`
 * runs synchronously and never re-enters this function, so one detached `<div>`
 * per render (instead of per block) is safe and saves an element allocation for
 * every block in the manuscript.
 */
let scratchHost: HTMLDivElement | null = null;

/** Set `data-*` handles on every top-level element of an HTML fragment. */
function applyBlockAttributes(html: string, attributes: Record<string, string>): string {
  const host = (scratchHost ??= document.createElement('div'));

  host.innerHTML = html;

  for (const element of Array.from(host.children)) {
    for (const [name, value] of Object.entries(attributes)) {
      element.setAttribute(name, value);
    }
  }

  return host.innerHTML;
}

/**
 * `renderContentValue('md', ...)` wraps a bare inline string (no block-level
 * markdown) in a `<p>` -- fine inside a paragraph block, but not when the
 * same HTML is being placed inside a heading tag. Unwrap that single `<p>`
 * so `<h2>` doesn't end up holding a nested block element.
 */
function unwrapSingleParagraph(html: string): string {
  const host = (scratchHost ??= document.createElement('div'));

  host.innerHTML = html;

  const [only, ...rest] = Array.from(host.children);

  return only && rest.length === 0 && only.tagName === 'P' ? only.innerHTML : html;
}

function baseAttributes(block: Block, index: number): Record<string, string> {
  const attributes: Record<string, string> = {
    'data-block-key': blockKey(block, index),
    'data-block-type': block.type ?? 'unknown',
  };

  if (block.role) attributes['data-role'] = block.role;
  if (block.parent) attributes['data-parent'] = block.parent;
  if (block.label) attributes['data-label'] = block.label;
  return attributes;
}

function attributeString(attributes: Record<string, string>): string {
  return Object.entries(attributes)
    .map(([name, value]) => `${name}="${escapeAttribute(value)}"`)
    .join(' ');
}

/**
 * Caption/label line for a table or figure. `data-part` lets CSS style it
 * without a class, which ProseMirror would strip.
 */
function renderCaption(block: Block, index: number, part: 'label' | 'caption'): string {
  const label = block.label ? `<strong>${escapeText(block.label)}</strong>` : '';
  const caption = block.caption ? escapeText(block.caption) : '';
  const text = [label, caption].filter(Boolean).join('. ');

  if (!text) return '';

  const attributes = { ...baseAttributes(block, index), 'data-part': part };

  return `<p ${attributeString(attributes)}>${text}</p>`;
}

function renderParagraph(block: Block, index: number): string {
  if (!block.content) return '';
  const attributes = baseAttributes(block, index);

  if (block.role === 'article-title') {
    return `<h1 ${attributeString(attributes)}>${sanitizeHtml(block.content.value)}</h1>`;
  }

  const html = renderContentValue(block.content.type, block.content.value);

  return applyBlockAttributes(html, attributes);
}

function renderSection(block: Block, index: number, levels: Map<string, number>): string {
  if (!block.title && !block.content) return '';

  // `content` is the heading's own formatted markup (md/html) -- it has
  // replaced `title` as the render source. `title` may still carry that same
  // markup (e.g. `<strong>Funding</strong>`), but every `parent` reference
  // -- on both section and paragraph blocks -- is plain text, so hierarchy
  // lookups go through `plainText` while rendering doesn't.
  const attributes = baseAttributes(block, index);
  const level = block.title ? (levels.get(plainText(block.title)) ?? 2) : 2;
  const headingAttributes = { ...attributes, 'data-part': 'heading' };
  const headingHtml = block.content
    ? unwrapSingleParagraph(renderContentValue(block.content.type, block.content.value))
    : escapeText(plainText(block.title as string));

  return `<h${level} ${attributeString(headingAttributes)}>${headingHtml}</h${level}>`;
}

function renderTable(block: Block, index: number): string {
  const parts = [renderCaption(block, index, 'caption')];

  if (block.content) {
    const html = renderContentValue(block.content.type, block.content.value);

    parts.push(applyBlockAttributes(html, baseAttributes(block, index)));
  }

  return parts.filter(Boolean).join('\n');
}

function renderFigure(block: Block, index: number, options: RenderOptions): string {
  const attributes = baseAttributes(block, index);
  const parts: string[] = [];
  const alt = block.alt ?? block.caption ?? block.label ?? 'Figure';
  const href = block.href ? (options.resolveHref?.(block.href) ?? block.href) : null;

  // `href` travels as `data-href`, never `src` — the FigureImage node view
  // reads it from there and is the only place a real `<img src>` gets set
  // (loadable as-is, or signed via the document's media-access grant). `src`
  // is suppressed at the schema level too (see FigureImage.ts), so an
  // unresolved `s3://` href never ends up as a real `src` anywhere, even
  // transiently in ProseMirror's own DOM handling.
  if (href) {
    parts.push(
      `<img ${attributeString(attributes)} data-href="${escapeAttribute(href)}" alt="${escapeAttribute(alt)}" />`
    );
  }

  parts.push(renderCaption(block, index, 'caption'));
  return parts.filter(Boolean).join('\n');
}

function renderFormula(block: Block, index: number): string {
  const attributes = baseAttributes(block, index);

  if (block.content?.type === 'latex') {
    attributes['data-math'] = block.content.value;
    if (block.label) attributes['data-math-label'] = block.label;
    return `<div ${attributeString(attributes)}></div>`;
  }

  // MathML-only formula, or an unexpected content format.
  if (block.mathml) {
    return applyBlockAttributes(sanitizeHtml(block.mathml), attributes);
  }
  if (block.content) {
    return applyBlockAttributes(
      renderContentValue(block.content.type, block.content.value),
      attributes
    );
  }
  return '';
}

/**
 * Fallback for `type: null` — the contract allows it for blocks the extractor
 * could not classify. Render the text rather than dropping it.
 */
function renderUnknown(block: Block, index: number): string {
  const attributes = { ...baseAttributes(block, index), 'data-block-type': 'unknown' };

  if (block.title) {
    return `<h2 ${attributeString(attributes)}>${escapeText(block.title)}</h2>`;
  }
  if (block.content) {
    return applyBlockAttributes(
      renderContentValue(block.content.type, block.content.value),
      attributes
    );
  }
  return '';
}

export function renderBlock(
  block: Block,
  index: number,
  levels: Map<string, number>,
  options: RenderOptions
): string {
  switch (block.type) {
    case 'paragraph':
      return renderParagraph(block, index);
    case 'section':
      return renderSection(block, index, levels);
    case 'table':
      return renderTable(block, index);
    case 'figure':
      return renderFigure(block, index, options);
    case 'formula':
      return renderFormula(block, index);
    default:
      return renderUnknown(block, index);
  }
}

/**
 * Inline placeholder for a block that failed schema validation or crashed its
 * renderer. Emitted as a `<p>` -- like every other block, this stays within
 * ProseMirror's schema, which discards wrapper `<div>`s and unknown attributes
 * (see file header) -- so it keeps its `data-block-key` handle and the rest of
 * the document renders normally around it.
 */
function renderBlockFallback(block: unknown, index: number, message: string): string {
  const key =
    block && typeof block === 'object' ? blockKey(block as Block, index) : `b${index}`;
  const originalType =
    block && typeof block === 'object' && 'type' in block
      ? String((block as Block).type ?? 'unknown')
      : 'unknown';
  const attributes = {
    'data-block-key': key,
    'data-block-type': 'error',
    'data-original-type': originalType,
    'data-error-message': message,
  };

  return `<p ${attributeString(attributes)}>This block could not be displayed.</p>`;
}

export function renderContract(
  contract: ManuscriptContract,
  options: RenderOptions = {}
): string {
  const shape = ManuscriptContractShapeSchema.safeParse(contract);

  if (!shape.success) {
    throw new Error(`Invalid manuscript contract: ${formatZodError(shape.error)}`);
  }

  const levels = computeHeadingLevels(contract.blocks);

  return contract.blocks
    .map((block, index) => {
      const parsed = BlockSchema.safeParse(block);

      if (!parsed.success) {
        const message = formatZodError(parsed.error);

        options.onBlockIssue?.({
          index,
          key: blockKey(block, index),
          type: block?.type ?? null,
          reason: 'invalid_schema',
          message,
        });
        return renderBlockFallback(block, index, message);
      }

      try {
        return renderBlock(parsed.data as Block, index, levels, options);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);

        options.onBlockIssue?.({
          index,
          key: blockKey(block, index),
          type: block?.type ?? null,
          reason: 'render_error',
          message,
        });
        return renderBlockFallback(block, index, message);
      }
    })
    .filter(Boolean)
    .join('\n');
}
